<template>
  <div v-if="displayedNotifications.length > 0" class="order-food-notify">
    <transition-group name="slide-down" tag="div">
      <div
        v-for="item in displayedNotifications"
        :key="item.id"
        class="order-food-notify__item"
        :class="`order-food-notify__item--${item.type}`"
      >
        <div class="order-food-notify__icon">
          <i class="icon-notify"></i>
        </div>
        <div class="order-food-notify__content">
          <span class="order-food-notify__type">{{ item.typeLabel }}</span>
          <span class="order-food-notify__order-id">{{ item.orderId }}</span>
          <span class="order-food-notify__separator">|</span>
          <span class="order-food-notify__dining-info" :class="[item.type !== 'new'? 'no-margin' : '']">{{ item.diningInfo }}</span>
          <span class="order-food-notify__hint">{{ item.type === 'new' ? $t('OrderFoodNotify.ConfirmOrderHint') : '' }}。</span>
        </div>
        <div class="order-food-notify__actions">
          <template v-if="item.type === 'new'">
            <button class="order-food-notify__btn order-food-notify__btn--secondary" @click="dismiss(item.id)">
              {{ $t('OrderFoodNotify.BtnAcknowledge') }}
            </button>
            <button class="order-food-notify__btn order-food-notify__btn--primary" @click="viewOrder(item.id)">
              {{ $t('OrderFoodNotify.BtnViewOrder') }}
            </button>
          </template>
          <template v-else>
            <button class="order-food-notify__btn order-food-notify__btn--primary" @click="dismiss(item.id)">
              {{ $t('OrderFoodNotify.BtnAcknowledge') }}
            </button>
          </template>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { DiningOption } from '@/js/utils/orderFood'
import { playOrderNotify } from '@/js/utils/audioManager'

const NOTIFICATION_TYPE = {
  NEW_ORDER: 4, // 新訂單
  CANCEL_ORDER: 14 // 取消訂單
}

export default {
  name: 'OrderFoodNotify',
  data () {
    return {
      notifications: [],
      /** 已顯示過的訂單 ID 集合，用於篩掉重複資料 */
      shownOrderIds: new Set()
    }
  },
  computed: {
    // 顯示最新的 5 筆推播
    displayedNotifications () {
      return this.notifications.slice(0, 5)
    }
  },
  mounted () {
    const saved = localStorage.getItem('orderFoodPendingNotifications')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) {
          const restored = parsed.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
          this.notifications = restored
          restored.forEach(n => this.shownOrderIds.add(n.id))
        }
      } catch {}
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', this.onServiceWorkerMessage)
    }
  },
  beforeDestroy () {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', this.onServiceWorkerMessage)
    }
  },
  methods: {
    onServiceWorkerMessage (event) {
      if (!event.data || event.data.FeatureName !== 'communityTableFoodForm') return

      let parsedData
      try {
        parsedData = JSON.parse(event.data.Data)
      } catch {
        return
      }
      // 取得訂單相關資料
      const { NotificationTypeId, ComOrderId, DiningOption: diningOptionValue, DiningOptionName, ReservationAtUtc, TableNo } = parsedData
      // 如果不是新訂單或取消訂單則不處理
      if (NotificationTypeId !== NOTIFICATION_TYPE.NEW_ORDER && NotificationTypeId !== NOTIFICATION_TYPE.CANCEL_ORDER) return

      // 相同訂單相同狀態不重複顯示
      const notifyKey = `${ComOrderId}-${NotificationTypeId}`
      if (this.shownOrderIds.has(notifyKey)) return
      this.shownOrderIds.add(notifyKey)

      const type = NotificationTypeId === NOTIFICATION_TYPE.NEW_ORDER ? 'new' : 'cancel'
      const typeLabel = type === 'new' ? '新訂單' : '取消訂單'
      const orderId = this.formatOrderId(ComOrderId)
      // 判斷是預約或內用
      const diningInfo = diningOptionValue === DiningOption.Reservation
        ? dayjs(ReservationAtUtc).format('YYYY/MM/DD HH:mm')
        : `${DiningOptionName} ${TableNo} `

      this.notifications.push({
        id: notifyKey,
        type,
        typeLabel,
        orderId: `編號 ${orderId}`,
        diningInfo,
        createdAt: Date.now()
      })
      this.notifications.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
      this._saveToLocalStorage()
      this.$store.commit('triggerOnlineOrderRefresh')
      // 播放點餐通知音效（多筆同時到達只播一輪）
      playOrderNotify()
    },
    /**
     * 取得顯示的訂單編號
     * @param comOrderId 訂單編號
     */
    formatOrderId (comOrderId) {
      if (!comOrderId || comOrderId.length < 8) return `#${comOrderId}`
      const monthDay = comOrderId.slice(4, 8)
      const seq = comOrderId.slice(-4)
      return `#${monthDay}${seq}`
    },
    /**
     * 移除資料
     * @param id 訂單編號(搭配狀態)
     */
    _saveToLocalStorage () {
      localStorage.setItem('orderFoodPendingNotifications', JSON.stringify(this.notifications))
    },
    dismiss (id) {
      this.notifications = this.notifications.filter(n => n.id !== id)
      this.shownOrderIds.delete(id)
      this._saveToLocalStorage()
    },
    /**
     * 前往餐點訂單頁
     * @param id 訂單編號(搭配狀態)
     */
    viewOrder (id) {
      this.dismiss(id)
      this.$router.push({ name: 'orderFoodManagement', query: { tab: 'online' } }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
*, *::before, *::after {
  box-sizing: border-box;
}

.order-food-notify {
  position: absolute;
  top: 16px;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;

  &__item {
    pointer-events: auto;
    display: flex;
    align-items: center;
    width: 1110px;
    padding: 10px 12px 10px 16px;
    border-radius: 8px;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.16);

    &:not(:last-child) {
      margin-bottom: 12px;
    }

    &--new {
      background-color: rgba(218, 240, 235, 0.8);
      color: #109084;

      .order-food-notify__btn--secondary {
        background-color: transparent;
        color: $mainColor;
        border: 1px solid $mainColor;

        &:hover { background-color: rgba($mainColor, 0.08); }
        &:active { background-color: rgba($mainColor, 0.18); }
      }

      .order-food-notify__btn--primary {
        background-color: $mainColor;
        color: $white;
        border: none;

        &:hover { filter: brightness(1.1); }
        &:active { filter: brightness(0.92); }
      }
    }

    &--cancel {
      background-color: rgba(255, 221, 214, 0.8);
      color: #df284b;

      .order-food-notify__btn--primary {
        color: inherit;
        background-color: transparent;
        border: 1px solid #df284b;

        &:hover { background-color: rgba(#df284b, 0.08); }
        &:active { background-color: rgba(#df284b, 0.18); }
      }
    }
  }

  &__icon {
    flex-shrink: 0;
    margin-right: 12px;

    i {
      font-size: 20px;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    font-size: map-get($fontSize, small);
    font-weight: bold;
  }

  &__type {
    margin-right: 10px;
  }

  &__separator {
    margin: 0 6px;
  }

  &__dining-info {
    margin-right: 6px;

    &.no-margin {
      margin-right: 0;
    }
  }

  &__actions {
    flex-shrink: 0;
    display: flex;
    margin-left: 16px;
  }

  &__btn {
    cursor: pointer;
    outline: none;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: map-get($fontSize, small);
    font-weight: bold;

    &:not(:last-child) {
      margin-right: 12px;
    }
  }
}

// transition
.slide-down-enter-active {
  transition: all 0.4s ease-out;
}
.slide-down-leave-active {
  transition: all 0.3s ease-in;
}
.slide-down-enter {
  transform: translateY(-100%);
  opacity: 0;
}
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
